import {
  Alert,
  AlertBody,
  AlertIcon,
  Button,
  Pill,
  RiArrowLeftLine,
  RiCloseLine,
  RiErrorWarningLine,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetDraftPlotGroups } from '@/api/rest/resources/plot';
import { DraftPlot, DraftPlotGroup } from '@/api/rest/resources/types/plot';

import { useCreateDraftPlotGroup, useUpdateDraftPlotGroup } from '../hooks/useDraftPlotGroupActions';
import { AutocompleteCreateField } from './AutocompleteCreateField';

type NewGroupPanelProps = {
  plots: DraftPlot[];
  organizationId: string;
  onBack: () => void;
  onSaved: () => void;
};

export const NewGroupPanel = ({ plots, organizationId, onBack, onSaved }: NewGroupPanelProps) => {
  const { t } = useTranslation();
  const [groupName, setGroupName] = useState('');
  const [saveError, setSaveError] = useState(false);

  const { data: groupsData } = useGetDraftPlotGroups({ queryParams: { organization: organizationId } });
  const existingGroups = groupsData?.results ?? [];

  const { submit: createGroup, isSubmitting: isCreating } = useCreateDraftPlotGroup();
  const { submit: updateGroup, isSubmitting: isUpdating } = useUpdateDraftPlotGroup();
  const isSubmitting = isCreating || isUpdating;

  const totalAreaHa = (plots.reduce((sum, p) => sum + p.area, 0) / 10_000).toFixed(2);
  const plotIds = plots.map((p) => p.id);

  const handleCreate = async () => {
    setSaveError(false);
    try {
      await createGroup({
        name: groupName || t('shared.uploadPlots.newGroup.title'),
        organization: organizationId,
        draft_plot_ids: plotIds,
      });
      onSaved();
    } catch {
      setSaveError(true);
    }
  };

  const handleSelectExisting = async (group: DraftPlotGroup) => {
    setSaveError(false);
    try {
      await updateGroup(group.id, {
        draft_plot_ids: Array.from(new Set([...group.draft_plot_ids, ...plotIds])),
      });
      onSaved();
    } catch {
      setSaveError(true);
    }
  };

  return (
    <div className='flex h-full flex-col overflow-hidden'>
      {/* Header */}
      <div className='flex shrink-0 items-center gap-2 border-b border-neutral-black-12 px-4 py-3'>
        <button
          type='button'
          aria-label={t('shared.uploadPlots.newGroup.backAriaLabel')}
          onClick={onBack}
          className='shrink-0 text-text-secondary hover:text-text-primary'
        >
          <RiArrowLeftLine size={18} />
        </button>
        <span className='typography-body1Semibold flex-1 truncate'>{t('shared.uploadPlots.newGroup.title')}</span>
        <button
          type='button'
          aria-label={t('shared.uploadPlots.newGroup.closeAriaLabel')}
          onClick={onBack}
          className='text-text-secondary hover:text-text-primary'
        >
          <RiCloseLine size={18} />
        </button>
      </div>

      {/* Body */}
      <div className='flex-1 overflow-y-auto px-4 py-4'>
        <Stack spacing={3}>
          {saveError && (
            <Alert type='error'>
              <AlertIcon>
                <RiErrorWarningLine size={20} />
              </AlertIcon>
              <AlertBody>{t('shared.uploadPlots.newGroup.saveFailed')}</AlertBody>
            </Alert>
          )}

          {/* Group name */}
          <div className='flex flex-col gap-0.5 rounded-xl border border-neutral-black-12 px-4 py-3'>
            <span className='typography-overline text-text-secondary'>
              {t('shared.uploadPlots.newGroup.fields.groupName')}
            </span>
            <AutocompleteCreateField
              items={existingGroups}
              getItem={(group) => ({
                key: group.id,
                label: group.name,
                meta: t('shared.uploadPlots.addToGroupDialog.plotCount', { count: group.total_plots }),
              })}
              searchValue={groupName}
              onSearchValueChange={setGroupName}
              onSelectItem={handleSelectExisting}
              onCreateNew={handleCreate}
              disabled={isSubmitting}
              placeholder={t('shared.uploadPlots.newGroup.fields.namePlaceholder')}
              noResultsLabel={t('shared.uploadPlots.addToGroupDialog.noResults')}
              createOptionLabel={(name) => t('shared.uploadPlots.createOption', { name })}
              inputClassName='typography-body1 w-full bg-transparent outline-none placeholder:text-text-disabled'
            />
          </div>

          {/* Stats row */}
          <div className='grid grid-cols-2 gap-3'>
            <div className='flex flex-col gap-0.5 rounded-xl border border-neutral-black-12 px-4 py-3'>
              <span className='typography-overline text-text-secondary'>
                {t('shared.uploadPlots.newGroup.fields.plotArea')}
              </span>
              <span className='typography-body1'>{totalAreaHa} ha</span>
            </div>
            <div className='flex flex-col gap-0.5 rounded-xl border border-neutral-black-12 px-4 py-3'>
              <span className='typography-overline text-text-secondary'>
                {t('shared.uploadPlots.newGroup.fields.landscapeArea')}
              </span>
              <span className='typography-body1'>0 ha</span>
            </div>
          </div>

          {/* Plots table */}
          <div className='rounded-3xl bg-white-100'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('shared.uploadPlots.newGroup.columns.name')}</TableHead>
                  <TableHead>{t('shared.uploadPlots.newGroup.columns.type')}</TableHead>
                  <TableHead>{t('shared.uploadPlots.newGroup.columns.size')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plots.map((plot) => (
                  <TableRow key={plot.id}>
                    <TableCell className='typography-body2'>{plot.name}</TableCell>
                    <TableCell>
                      <Pill size='small' className='bg-neutral-black-8'>
                        {t(`global.plotTypes.${plot.land_type}`)}
                      </Pill>
                    </TableCell>
                    <TableCell className='typography-body2'>{(plot.area / 10_000).toFixed(2)} ha</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Stack>
      </div>

      {/* Footer */}
      <div className='flex shrink-0 items-center justify-between border-t border-neutral-black-12 px-4 py-3'>
        <Button variant='outline' onClick={onBack}>
          {t('global.ui.buttons.cancel')}
        </Button>
        <Button loading={isSubmitting} onClick={handleCreate}>
          {t('shared.uploadPlots.newGroup.saveButton')}
        </Button>
      </div>
    </div>
  );
};
